import React, { useContext } from 'react';
import { DefaultColumnFilter } from 'src/components/shared/TPEReactTable';
import { SegmentTablesContext } from 'src/components/segment-cdt/SegmentTablesContext';
import { ACTIONS } from 'src/services/segment-cdt/SegmentTablesReducer';
import { ColumnDef, SegmentRow } from 'src/models/segment-cdt/SegmentCdtTypes';

/**
 * Pure builders for the segment detail data grid — kept out of the component so column shaping and row
 * flattening are unit-testable without mounting. The grid's columns are the four row-level FRAMES
 * fields followed by one column per `ColumnDef`; a row's cell value is looked up by `columnId` from the
 * numeric `values` map or the `textValues` map (a column lives in exactly one of them).
 */

/**
 * Row-level identity/frame fields, in display order, ahead of the per-table value columns. `filterAttr`
 * is the stored DynamoDB attribute name the search backend expects (`CustomDataTableQuerySupport` passes
 * `attributeName` through untranslated) — the react-table accessor is camelCase but the attribute is
 * snake_case, so a filter must map through this or it silently matches nothing.
 */
const FIXED_COLUMNS: { accessor: string; header: string; filterAttr: string }[] = [
    { accessor: 'segmentValue', header: 'Segment Value', filterAttr: 'segment_value' },
    { accessor: 'segmentDescription', header: 'Segment Description', filterAttr: 'segment_description' },
    { accessor: 'status', header: 'Status', filterAttr: 'status' },
];

/**
 * The stored search attribute for a filterable (fixed) column, or undefined for a dynamic value column —
 * whose values live in the row's `values`/`text_values` maps, not as top-level attributes, so they can't
 * be filtered this way at all. A filter on such a column must be dropped rather than sent.
 */
export function filterAttribute(accessor: string): string | undefined {
    return FIXED_COLUMNS.find((c) => c.accessor === accessor)?.filterAttr;
}

/**
 * How many identity columns precede the value columns. Exported so a summary row can span exactly those
 * cells and keep its figures under the right value columns - deriving it from the same array the header is
 * built from means adding a fixed column cannot silently misalign the footer.
 */
export const FIXED_COLUMN_COUNT = FIXED_COLUMNS.length;

/**
 * Grid accessor for a dynamic value column. Namespaced so a user column whose `columnId` matches a frame
 * accessor (`status`, `segmentValue`, `segmentDescription`) can't emit a duplicate column
 * or, in `flattenRow`, overwrite the frame field with its value — which would render the wrong data in the
 * identity column. The prefix has no dot (so react-table treats it as a flat key, not a path) and no frame
 * accessor starts with it.
 */
const VALUE_ACCESSOR_PREFIX = 'valcol_';
export function valueAccessor(columnId: string): string {
    return VALUE_ACCESSOR_PREFIX + columnId;
}

/** Numbers get thousands separators; text renders as-is; missing cells render blank. */
function renderCell(value: any): JSX.Element {
    const text = value == null ? '' : typeof value === 'number' ? value.toLocaleString() : String(value);
    return <div className="cell-text">{text}</div>;
}

/**
 * What a value column shows when the backend holds no value for that column on that row.
 *
 * A blank cell is ambiguous in a numeric grid — it reads as plausibly zero, or as the grid having failed to
 * render — and the two mean opposite things for a column that is meant to be pulled or uploaded. A dash says
 * "nothing is stored here" in a way blank cannot.
 *
 * Exported so the pinned summary rows beneath the grid spell absence the same way (`segmentAggregateRows`).
 * They sit directly under these cells, in the same columns, so two spellings in one column would be read as
 * two different states.
 */
export const NO_VALUE = '-';

/**
 * A value column's cell. Absence is shown as {@link NO_VALUE}; everything present renders as
 * {@link renderCell} does.
 *
 * Only null / undefined / empty-string count as absent. A numeric `0` is a value and keeps rendering as `0`:
 * a pulled or entered zero is a factual claim about that segment, and showing it as a dash would erase the
 * difference between "the GL says zero" and "the GL was never asked".
 *
 * Applied to the value columns only, not to the identity columns beside them. There, blank is unambiguous —
 * a segment with no description simply has none — whereas a dash would dress a missing identity field up as a
 * deliberate "none", and `seedSegmentRow` writes all three on every row anyway.
 */
function renderValueCell(value: any): JSX.Element {
    if (value == null || value === '') {
        return <div className="cell-text segmentNoValue" title="No value">{NO_VALUE}</div>;
    }
    return renderCell(value);
}


/**
 * The affordance in a value column's heading that opens that column's configuration.
 *
 * <p>Obtains `dispatch` from `SegmentTablesContext` and fires the action itself, rather than taking a callback
 * from `buildColumnDefinitions`' caller. Being its own component is what makes that possible without a hook in
 * the `Header` thunk: the thunk stays a plain function returning JSX, so it can still be called directly (as
 * the column-shaping tests do) while the hook only runs when a header is actually rendered.
 *
 * <p>Renders nothing when there is no provider, which is the case for a caller that shapes columns outside the
 * segment feature. That is deliberate: no dispatch means no way to open the popup, and a button that cannot
 * act is worse than none.
 */
function ColumnConfigTrigger(props: { column: ColumnDef }) {
    const { column } = props;
    const context = useContext(SegmentTablesContext);
    const dispatch = context?.dispatch;
    if (!dispatch) {
        return null;
    }
    const label = `View configuration for ${column.displayName ?? column.columnId}`;
    return (
        <button
            type="button"
            className="segmentColumnConfigTrigger"
            title={label}
            aria-label={label}
            // Required even though value columns set disableSortBy: TPEReactTable spreads
            // getSortByToggleProps() unconditionally, so a bare click would still reach the sort toggle if
            // this were ever reused on a sortable column.
            onClick={(e) => { e.stopPropagation(); dispatch(ACTIONS.SET_DETAIL_CONFIG_COLUMN.withPayload(column)); }}
        >
            {'\u24D8'}
        </button>
    );
}

/** react-table column defs: the four fixed columns, then one per `ColumnDef` keyed by `columnId`. */
/**
 * @param columns the table's value columns, one grid column each
 */
export function buildColumnDefinitions(columns: ColumnDef[] = []): any[] {
    // Filter/hidePopover let the "Filter rows" toggle render an inline per-column filter input.
    const fixed = FIXED_COLUMNS.map((c) => ({
        accessor: c.accessor,
        Header: () => <div className="cell-text">{c.header}</div>,
        Cell: (e: any) => renderCell(e.value),
        Filter: DefaultColumnFilter,
        hidePopover: true,
    }));
    // Dynamic value columns are neither sortable nor filterable server-side: the backend throws
    // UnsortableColumnException (400) for a user-defined sort field — and it sticks, 400-ing every later
    // page/filter request — and their values live in the values/text_values maps rather than as row
    // attributes, so a CONTAINS filter on them matches nothing. Two flags are needed for sort: `disableSortBy`
    // (react-table) makes the header click a no-op — TPEReactTable spreads getSortByToggleProps()
    // unconditionally, so without this a click would still fire a sort — and `suppressSorting`
    // (TPEReactTable's own flag) hides the now-dead caret. `suppressFiltering` likewise hides the filter
    // input. The sort/filter suppression matches the sibling RecordsGrid's treatment of user-defined columns;
    // the absent-value rendering deliberately does not — that grid leaves such a cell blank.
    const dynamic = columns
        .filter((c) => !!c.columnId)
        .map((c) => ({
            accessor: valueAccessor(c.columnId as string),
            // The affordance is a button beside the label, not the label itself: the heading is the sort
            // target on sibling grids, so making the whole cell clickable would conflate two actions.
            Header: () => (
                <div className="cell-text">
                    {c.displayName ?? c.columnId}
                    <ColumnConfigTrigger column={c} />
                </div>
            ),
            Cell: (e: any) => renderValueCell(e.value),
            disableSortBy: true,
            suppressSorting: true,
            suppressFiltering: true,
            hidePopover: true,
        }));
    return [...fixed, ...dynamic];
}

/**
 * Flatten a wire `SegmentRow` into the plain row object the grid renders. Frame fields keep their plain
 * accessors; each value column is stored under its namespaced `valueAccessor(columnId)` so a column whose
 * `columnId` equals a frame accessor can't overwrite the identity value.
 */
export function flattenRow(row: SegmentRow, columns: ColumnDef[] = []): Record<string, any> {
    const flat: Record<string, any> = {
        segmentValue: row.segmentValue,
        segmentDescription: row.segmentDescription,
        status: row.status,
    };
    columns.forEach((c) => {
        const id = c.columnId;
        if (!id) {
            return;
        }
        const numeric = row.values ? row.values[id] : undefined;
        const text = row.textValues ? row.textValues[id] : undefined;
        flat[valueAccessor(id)] = numeric != null ? numeric : text != null ? text : undefined;
    });
    return flat;
}
